import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { alpha, useTheme } from '@mui/material/styles';

export interface FilterOption {
  label: string;
  key: string;
  type?: 'checkbox' | 'range';
  options?: string[];
  min?: number;
  max?: number;
}

export interface FilterSearchProps {
  filterOptions?: FilterOption[];
  onSearch: (query: string) => void;
  onApplyFilters?: (filters: { [key: string]: any }) => void;
  searchText?: string;
  children?: React.ReactNode;
  hasChildren?: boolean;
  resetFilters?: () => void;
  hasFilters?: boolean;
}

export const FilterSearch: React.FC<FilterSearchProps> = ({
  filterOptions,
  onSearch,
  onApplyFilters,
  children,
  searchText,
  hasChildren,
  resetFilters,
  hasFilters = true,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isFilterOpen = Boolean(anchorEl);

  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: any }>({});
  const [rangeValues, setRangeValues] = useState<{
    [key: string]: { min?: number; max?: number };
  }>({});

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    setRangeValues({});
    resetFilters?.();
    onApplyFilters?.({});
  };

  const handleApplyFilters = () => {
    const filters = { ...selectedFilters };

    Object.keys(rangeValues).forEach((key) => {
      if (
        rangeValues[key]?.min !== undefined ||
        rangeValues[key]?.max !== undefined
      ) {
        filters[key] = rangeValues[key];
      }
    });

    onApplyFilters?.(filters);
    setAnchorEl(null);
  };

  const handleCheckboxChange = (filterKey: string, option: string) => {
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev };
      if (updatedFilters[filterKey]?.includes(option)) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (item: string) => item !== option,
        );
      } else {
        updatedFilters[filterKey] = [
          ...(updatedFilters[filterKey] || []),
          option,
        ];
      }
      if (updatedFilters[filterKey].length === 0) {
        delete updatedFilters[filterKey];
      }
      return updatedFilters;
    });
  };

  const handleRangeChange = (
    filterKey: string,
    value: string,
    isMin: boolean,
  ) => {
    setRangeValues((prev) => {
      const parsed = parseInt(value, 10);
      return {
        ...prev,
        [filterKey]: {
          ...(prev[filterKey] || {}),
          [isMin ? 'min' : 'max']: isNaN(parsed) ? undefined : parsed,
        },
      };
    });
  };

  const handleSelectAll = (filterKey: string, options: string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]:
        prev[filterKey]?.length === options.length ? [] : [...options],
    }));
  };

  const safeFilterOptions = Array.isArray(filterOptions) ? filterOptions : [];
  const [activeTab, setActiveTab] = useState<string>(
    safeFilterOptions[0]?.key || '',
  );

  const activeCount = Object.keys(selectedFilters).length;

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 520, gap: 1.5 }}>
      {/* Search Bar & Filter Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={searchText ?? 'Search...'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              bgcolor: 'background.paper',
              height: 40,
              fontSize: '0.875rem',
            },
          }}
        />

        {hasFilters && (
          <Button
            ref={filterButtonRef}
            variant="outlined"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={<TuneRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 40,
              px: 2,
              borderRadius: '14px',
              borderColor: activeCount > 0 ? 'primary.main' : 'divider',
              color: activeCount > 0 ? 'primary.main' : 'text.primary',
              bgcolor: activeCount > 0 ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              fontWeight: 600,
              fontSize: '0.8125rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            Filter
            {activeCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.75,
                  py: 0.1,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  borderRadius: '9999px',
                  bgcolor: 'primary.main',
                  color: '#ffffff',
                }}
              >
                {activeCount}
              </Box>
            )}
          </Button>
        )}
      </Box>

      {/* Filter Popover */}
      <Popover
        open={isFilterOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 540 },
            maxWidth: '100vw',
            borderRadius: '18px',
            border: '1px solid',
            borderColor: 'divider',
            mt: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 40px -8px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary' }}>
            Filters
          </Typography>
          <Chip
            size="small"
            label={`${activeCount} Active`}
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              borderRadius: '9999px',
            }}
          />
        </Box>

        {/* Content */}
        {hasChildren ? (
          <Box sx={{ p: 2, maxHeight: 420, overflowY: 'auto' }}>{children}</Box>
        ) : (
          <Box sx={{ display: 'flex', minHeight: 260, maxHeight: 380 }}>
            {/* Left Category Tabs */}
            <Box
              sx={{
                width: '38%',
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                overflowY: 'auto',
                p: 1,
              }}
            >
              <List disablePadding>
                {safeFilterOptions.map((filter) => (
                  <ListItemButton
                    key={filter.key}
                    selected={activeTab === filter.key}
                    onClick={() => setActiveTab(filter.key)}
                    sx={{
                      borderRadius: '10px',
                      py: 1,
                      px: 1.5,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'background.paper',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        '& .MuiListItemText-primary': {
                          fontWeight: 700,
                          color: 'primary.main',
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={filter.label}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* Right Options Panel */}
            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
              {safeFilterOptions.map(
                (filter) =>
                  activeTab === filter.key && (
                    <Box key={filter.key}>
                      {filter.type === 'checkbox' && filter.options && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedFilters[filter.key]?.length === filter.options.length}
                                  onChange={() => handleSelectAll(filter.key, filter.options!)}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                                  Select All
                                </Typography>
                              }
                            />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                              {selectedFilters[filter.key]?.length || 0} / {filter.options.length}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                            {filter.options.map((option) => (
                              <FormControlLabel
                                key={option}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedFilters[filter.key]?.includes(option) || false}
                                    onChange={() => handleCheckboxChange(filter.key, option)}
                                  />
                                }
                                label={
                                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
                                    {option}
                                  </Typography>
                                }
                                sx={{
                                  m: 0,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: '8px',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {filter.type === 'range' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
                            Range Selection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Min"
                              value={rangeValues[filter.key]?.min ?? filter.min ?? ''}
                              onChange={(e) => handleRangeChange(filter.key, e.target.value, true)}
                              sx={{ flex: 1 }}
                            />
                            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>to</Typography>
                            <TextField
                              size="small"
                              type="number"
                              label="Max"
                              value={rangeValues[filter.key]?.max ?? filter.max ?? ''}
                              onChange={(e) => handleRangeChange(filter.key, e.target.value, false)}
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ),
              )}
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Button
            size="small"
            onClick={handleClearFilters}
            startIcon={<RestartAltRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': { color: 'text.primary' },
            }}
          >
            Clear all
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setAnchorEl(null)}
              startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2,
                boxShadow: 'none',
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
